import React, { useEffect, useState } from "react";
import { Upload, Form, Button, Input, Drawer, Select, Tabs, Tag } from "antd";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import { UploadFile, RcFile, UploadProps } from "antd/es/upload/interface";
import moment from "moment";
import { toast } from "sonner";

import { uploadUrl } from "@/config/urls";
import { CustomIcons } from "@/assets/index";
import MomentAntdDateTimePicker from "@/components/widgets/DateTimePicker";
import { usePanicTypes } from "@/hooks/usePanicTypes";
import { useEditResponder } from "@/hooks/useResponder";
import CommonUserFields from "../Forms/CommonUserFields";
import { createAxiosUploadProps } from "@/util/helpers";

const { Option } = Select;
const { TabPane } = Tabs;

interface EditResponderDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  editDetails: IResponderDetails | null;
}

// Filter `option.label` match the user type `input`
const filterOption = (
  input: string,
  option?: { id?: number; label: string; value: string } | any
) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const dateFormat = "YYYY-MM-DD";

function EditResponderDrawer({
  isOpen,
  onClose,
  editDetails,
}: EditResponderDrawerProps) {
  const panicTypes = usePanicTypes();
  const useEditMutation = useEditResponder();
  const [form] = Form.useForm();
  const [medicalLicenseUri, setMedicalLicenseUri] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [selectedPanicTypes, setSelectedPanicTypes] = useState<
    IResponderResponsePanicTypes[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(true);
  const [isPsiraValid, setIsPsiraValid] = useState(true);
  const [activeTabKey, setActiveTabKey] = useState("1");

  const isValidDate = (validToDate: string | Date) => {
    return moment(validToDate).isAfter(moment());
  };

  const handleReset = () => {
    form.resetFields();
    setSelectedPanicTypes([]);
    setMedicalLicenseUri("");
    setFileList([]);
    setActiveTabKey("1");
    setIsLicenseValid(true);
    setIsPsiraValid(true);
    setIsUploading(false);
    useEditMutation.reset();
  };

  // Use useEffect to set initial values when editing
  useEffect(() => {
    if (isOpen) {
      if (editDetails) {
        form.setFieldsValue(editDetails);

        // Update phone number with prefix
        if (editDetails.phoneNumber) {
          const prefix = editDetails.phoneNumber.slice(0, 3);
          form.setFieldsValue({ prefix });
          form.setFieldsValue({
            phoneNumber: editDetails.phoneNumber.slice(3),
          });
        }

        // Update medical license and file list
        if (editDetails.medicalLicense) {
          setMedicalLicenseUri(editDetails.medicalLicense);
          const initialFileList = [
            {
              uid: "-1",
              name: "License.pdf",
              status: "done",
              url: editDetails.medicalLicense,
            },
          ] as const;
          setFileList([...initialFileList]);
          form.setFieldsValue({
            medicalLicense: initialFileList,
          });
        }

        // Update panic types
        if (editDetails && editDetails.panicTypes) {
          setSelectedPanicTypes(editDetails.panicTypes);
          form.setFieldsValue({
            panicTypes: editDetails.panicTypes.map((pt) => pt.id),
          });
          editDetails.panicTypes.forEach((pt) => {
            form.setFieldsValue({
              [`status-${pt.id}`]: pt.status,
              [`reason-${pt.id}`]: pt.reason,
            });
          });
        }

        if (editDetails.companyId) {
          form.setFieldsValue({ companyId: editDetails.companyId });
        }

        // Format dates
        const formattedDateOfBirth = editDetails.dateOfBirth
          ? moment(editDetails.dateOfBirth, dateFormat)
          : null;
        form.setFieldsValue({
          dateOfBirth: formattedDateOfBirth,
        });

        if (editDetails.medicalLicenseExpiryDate) {
          const formattedValidToDate = moment(
            editDetails.medicalLicenseExpiryDate,
            dateFormat
          );
          form.setFieldsValue({
            medicalLicenseExpiryDate: formattedValidToDate,
          });
          const isValid = isValidDate(editDetails.medicalLicenseExpiryDate);
          setIsPsiraValid(isValid);
          if (!isValid) {
            form.setFieldsValue({
              status: "Expired",
            });
          }
        }

        if (editDetails.psiraExpiryDate) {
          const formattedValidToDate = moment(
            editDetails.psiraExpiryDate,
            dateFormat
          );
          form.setFieldsValue({
            psiraExpiryDate: formattedValidToDate,
          });
          const isValid = isValidDate(editDetails.psiraExpiryDate);
          setIsLicenseValid(isValid);
          if (!isValid) {
            form.setFieldsValue({
              status: "Expired",
            });
          }
        }
      } else {
        handleReset();
      }
    } else {
      handleReset();
    }
  }, [isOpen, editDetails, form]);

  const handleClose = async () => {
    handleReset();
    onClose();
  };

  const handleTabChange = (key: string) => {
    setActiveTabKey(key);
  };

  const beforeCertificateUpload = (file: RcFile) => {
    const isPdfOrWord = ["application/pdf"].includes(file.type);
    const isLt15M = file.size / 1024 / 1024 < 15;
    if (!isPdfOrWord) {
      toast.error("You can only upload PDF file!");
      return false;
    }
    if (!isLt15M) {
      toast.error("file must smaller than 15MB!");
      return false;
    }
    return isPdfOrWord && isLt15M;
  };

  const certificateUploadProps: UploadProps = {
    ...createAxiosUploadProps("/util/post/document"),
    onChange(info) {
      const lastSuccessfulUpload = fileList;
      const currentFile = info.file;
      const newFileList = [...info.fileList];

      setFileList(newFileList);

      if (currentFile.status === "uploading") {
        setIsUploading(true);
      } else if (currentFile.status === "done") {
        setIsUploading(false);
        setFileList([currentFile]);
        setMedicalLicenseUri(currentFile.response.path);
        form.setFieldsValue({ medicalLicense: currentFile.response.path });
        toast.success(`${currentFile.name} file uploaded successfully.`);
      } else if (currentFile.status === "error") {
        setIsUploading(false);
        toast.error(`${currentFile.name} file upload failed.`);
        if (lastSuccessfulUpload) {
          setFileList(lastSuccessfulUpload);
        } else {
          setFileList([]);
        }
      }
    },
    beforeUpload: beforeCertificateUpload,
    fileList,
    accept: ".pdf",
  };

  const handlePanicTypeChange = (selectedIds: number[]) => {
    const updatedPanicTypes = selectedIds.map((id) => {
      const existingType = selectedPanicTypes.find((pt) => pt.id === id);
      return (
        existingType || {
          id,
          name: panicTypes.data?.find((pt) => pt.id === id)?.name || "",
          status: "Active",
          reason: "",
        }
      );
    }) as IResponderResponsePanicTypes[];
    setSelectedPanicTypes(updatedPanicTypes);
  };

  const showActiveStatus = (pT: IResponderResponsePanicTypes): boolean => {
    const nameLower = pT.name.toLowerCase();

    if (nameLower.includes("medical")) {
      return isLicenseValid && isPsiraValid;
    }

    if (nameLower.includes("security")) {
      return isPsiraValid;
    }

    return true;
  };

  const onFinish = async (formValues: any) => {
    // Validate all tabs before submitting
    try {
      await form.validateFields();
    } catch (error) {
      toast.error("Please fill in all required fields across all tabs");
      return;
    }

    const { prefix, phoneNumber, psiraNumber, ...rest } = formValues;
    const fullPhoneNumber = `${prefix}${phoneNumber}`;

    const panicTypesInfo = selectedPanicTypes.map((pt) => {
      return {
        Id: pt.id,
        Status: formValues[`status-${pt.id}`] || "Active",
        Reason: formValues[`reason-${pt.id}`] || "",
      };
    });

    const submissionData = {
      ...editDetails,
      fullPhoneNumber: editDetails?.phoneNumber || "",
      PanicTypes: panicTypesInfo,
      psira: psiraNumber || editDetails?.psiraNumber,
      companyId: editDetails?.companyId || 0,
      userId: editDetails?.userId || 0,
    } as IEditResponder;

    try {
      await useEditMutation.mutateAsync(submissionData);
      toast.success("Responder updated successfully");
      handleClose();
    } catch (error) {
      console.error(error);
    }
  };

  const onValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues.panicTypes) {
      handlePanicTypeChange(changedValues.panicTypes);
    }
    if (changedValues.medicalLicenseExpiryDate) {
      const isValid = isValidDate(changedValues.medicalLicenseExpiryDate);
      setIsLicenseValid(isValid);
    }
    if (changedValues.psiraExpiryDate) {
      const isValid = isValidDate(changedValues.psiraExpiryDate);
      setIsPsiraValid(isValid);
    }
  };

  const showPRISAFields = selectedPanicTypes.some(
    (pt) =>
      pt.name.toLowerCase().includes("security") ||
      pt.name.toLowerCase().includes("medical")
  );
  const showMedicalFields = selectedPanicTypes.some((pt) =>
    pt.name.toLowerCase().includes("medical")
  );

  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Drawer
      title="View Responder"
      placement="right"
      onClose={handleClose}
      open={isOpen}
      width={600}
      closeIcon={
        <CustomIcons.CloseIcon
          width={24}
          height={24}
          viewBox={null}
          svgColor="#1C274C"
        />
      }
    >
      <Form
        form={form}
        name="editResponder"
        onFinish={onFinish}
        layout="vertical"
        className="grid w-full"
        onValuesChange={onValuesChange}
      >
        <Tabs
          activeKey={activeTabKey}
          onChange={handleTabChange}
          className="w-full"
        >
          <TabPane tab="Personal Details" key="1" className="grid w-full">
            <div className="grid grid-col">
              <CommonUserFields
                form={form}
                useMomentDatePicker
                readonly={true}
              />
              <div className="flex flex-col w-full pl-3">
                <Form.Item
                  name="panicTypes"
                  label="Type of Panic"
                  className="mb-4"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please select panics the responder can respond to!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select panics responder can respond to"
                    size="large"
                    mode="multiple"
                    loading={panicTypes.isLoading}
                    filterOption={filterOption}
                    placement="topLeft"
                    disabled={true}
                  >
                    {panicTypes.data?.map((item: IPanicTypes) => (
                      <Select.Option
                        key={`key-panicTypes-${item.id}`}
                        value={item.id}
                      >
                        {item.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                {editDetails?.idNumber && (
                  <div className="flex flex-row w-full gap-2 justify-between">
                    <Form.Item name="idNumber" label="ID Number">
                      <Input
                        size="large"
                        placeholder="237838345773"
                        className="w-full"
                        readOnly={true}
                      />
                    </Form.Item>
                  </div>
                )}

                {editDetails?.medicalLicenseExpiryDate && (
                  <div className="flex flex-row w-full gap-2 justify-between">
                    <Form.Item
                      name="medicalLicenseExpiryDate"
                      label="Medical License Expiry Date"
                    >
                      <Input
                        size="large"
                        placeholder="2025-06-12"
                        className="w-full"
                        readOnly={true}
                      />
                    </Form.Item>
                  </div>
                )}

                {editDetails?.passportNumber && editDetails?.permitNumber && (
                  <div className="flex flex-row w-full gap-2 justify-between">
                    <Form.Item name="passportNumber" label="Passport Number">
                      <Input
                        size="large"
                        placeholder="237838345773"
                        className="w-full"
                        readOnly={true}
                      />
                    </Form.Item>
                    <Form.Item name="permitNumber" label="Permit Number">
                      <Input
                        size="large"
                        placeholder="237838345773"
                        className="w-full"
                        readOnly={true}
                      />
                    </Form.Item>
                  </div>
                )}

                {editDetails?.psiraExpiryDate && (
                  <div className="flex flex-row w-full gap-2 justify-between">
                    <Form.Item
                      name="psiraNumber"
                      label="PSIRA For Responder"
                      rules={[
                        {
                          required: true,
                          message: "Please input the responder psira number!",
                        },
                        {
                          pattern: /^\d{7,}$/,
                          message:
                            "Please enter a valid PSIRA number. It should be at least 7 digits long.",
                        },
                      ]}
                      className="mb-4"
                    >
                      <Input
                        size="large"
                        placeholder="237838345773"
                        className="w-1/2"
                        readOnly={true}
                      />
                    </Form.Item>

                    <Form.Item
                      name="psiraExpiryDate"
                      label="PSIRA for Responder Valid To"
                      rules={[
                        {
                          required: true,
                          message: "Please enter the responder psira valid to!",
                        },
                      ]}
                      className="mb-4"
                    >
                      <MomentAntdDateTimePicker
                        size="large"
                        style={{ width: "100%" }}
                        className="w-1/2"
                        readOnly={true}
                        disabled={true}
                      />
                    </Form.Item>
                  </div>
                )}
              </div>
            </div>
          </TabPane>

          <TabPane tab="Docs" key="2">
            <div className="grid grid-cols-2 gap-4">
              {editDetails?.idNumberUrl && (
                <Form.Item label="Copy of ID" className="mb-4">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    className="w-full"
                    onClick={() =>
                      handleDownload(editDetails.idNumberUrl!, "copy-of-id.pdf")
                    }
                  >
                    Download Copy of ID
                  </Button>
                </Form.Item>
              )}

              {editDetails?.passportNumberUrl && (
                <Form.Item label="Copy of Passport" className="mb-4">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    className="w-full"
                    onClick={() =>
                      handleDownload(
                        editDetails.passportNumberUrl!,
                        "copy-of-passport.pdf"
                      )
                    }
                  >
                    Download Copy of Passport
                  </Button>
                </Form.Item>
              )}

              {editDetails?.permitNumberUrl && (
                <Form.Item label="Copy of Permit" className="mb-4">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    className="w-full"
                    onClick={() =>
                      handleDownload(
                        editDetails.permitNumberUrl!,
                        "copy-of-permit.pdf"
                      )
                    }
                  >
                    Download Copy of Permit
                  </Button>
                </Form.Item>
              )}

              {editDetails?.securityTrainningCertificateUrl && (
                <Form.Item label="Security Certificate" className="mb-4">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    className="w-full"
                    onClick={() =>
                      handleDownload(
                        editDetails.securityTrainningCertificateUrl!,
                        "security-certificate.pdf"
                      )
                    }
                  >
                    Download Security Certificate
                  </Button>
                </Form.Item>
              )}

              {editDetails?.psiraCertificate && (
                <Form.Item label="Psira Certificate" className="mb-4">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    className="w-full"
                    onClick={() =>
                      handleDownload(
                        editDetails.psiraCertificate!,
                        "psira-certificate.pdf"
                      )
                    }
                  >
                    Download Psira Certificate
                  </Button>
                </Form.Item>
              )}

              {editDetails?.criminalRecordClearanceUrl && (
                <Form.Item label="Criminal Record Clearance" className="mb-4">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    className="w-full"
                    onClick={() =>
                      handleDownload(
                        editDetails.criminalRecordClearanceUrl!,
                        "criminal-record-clearance.pdf"
                      )
                    }
                  >
                    Download Criminal Record Clearance
                  </Button>
                </Form.Item>
              )}

              {editDetails?.firearmsCompetenciesUrl && (
                <Form.Item label="Firearms Competencies" className="mb-4">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    className="w-full"
                    onClick={() =>
                      handleDownload(
                        editDetails.firearmsCompetenciesUrl!,
                        "firearms-competencies.pdf"
                      )
                    }
                  >
                    Download Firearms Competencies
                  </Button>
                </Form.Item>
              )}

              {editDetails?.proofOfEmploymentUrl && (
                <Form.Item label="Proof of Employment" className="mb-4">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    className="w-full"
                    onClick={() =>
                      handleDownload(
                        editDetails.proofOfEmploymentUrl!,
                        "proof-of-employment.pdf"
                      )
                    }
                  >
                    Download Proof of Employment
                  </Button>
                </Form.Item>
              )}

              {editDetails?.medicalLicense && (
                <Form.Item label="Medical License" className="mb-4">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    className="w-full"
                    onClick={() =>
                      handleDownload(
                        editDetails.medicalLicense!,
                        "medical-license.pdf"
                      )
                    }
                  >
                    Download Medical License
                  </Button>
                </Form.Item>
              )}
            </div>
          </TabPane>

          <TabPane tab="Status" key="3">
            {selectedPanicTypes.map((pt: IResponderResponsePanicTypes) => (
              <div key={pt.id} className="mb-6">
                <div className="flex flex-col gap-4">
                  <Form.Item
                    name={`status-${pt.id}`}
                    label={`Status for ${pt.name}`}
                    rules={[
                      { required: true, message: "Please select a status" },
                    ]}
                    className="mb-4"
                  >
                    <Select
                      placeholder="Select responder's status"
                      size="large"
                    >
                      {showActiveStatus(pt) && (
                        <Option value="Active">
                          <Tag color="green">Active</Tag>
                        </Option>
                      )}
                      <Option value="Expired">
                        <Tag color="yellow">Expired</Tag>
                      </Option>
                      <Option value="Suspended">
                        <Tag color="red">Suspended</Tag>
                      </Option>
                    </Select>
                  </Form.Item>

                  {!showActiveStatus(pt) && (
                    <span
                      style={{ color: "darkred", fontSize: 12 }}
                      className="mb-2"
                    >
                      Valid to dates has expired
                    </span>
                  )}

                  <Form.Item
                    name={`reason-${pt.id}`}
                    label={`Reason for ${pt.name}`}
                    rules={[
                      {
                        required: true,
                        message: `Please enter a reason for ${pt.name}`,
                      },
                    ]}
                    className="mb-4"
                  >
                    <Input.TextArea
                      placeholder="This is the description of your decision. It should be precise."
                      rows={3}
                      count={{
                        show: true,
                        max: 200,
                        strategy: (txt) => txt.length,
                        exceedFormatter: (txt, { max }) => txt.slice(0, max),
                      }}
                    />
                  </Form.Item>
                </div>
              </div>
            ))}
            <div className="flex gap-2 justify-end">
              <Button type="default" onClick={handleClose} size="large">
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => form.submit()}
                size="large"
                className="bg-[#1757a2]"
                disabled={isUploading}
                loading={useEditMutation.isPending}
              >
                Submit
              </Button>
            </div>
          </TabPane>
        </Tabs>
      </Form>
    </Drawer>
  );
}

export default EditResponderDrawer;
